import { FC, useContext, useState } from "react";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { useI18n } from "../../hooks/useTranslations";
import { useAdminMileagesApi } from "../../http/mileages/api";
import { User } from "../../http/users/types";
import { formatDateNumeric } from "../../types/utils";
import { InfoBox } from "../ui/Boxes";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import Input from "../ui/Input";
import ModalDialog from "../ui/ModalDialog";
import Switch from "../ui/Switch";

type SendMileageReminderDialogProps = {
  carId: string;
  driver: Pick<User, "fullName" | "email" | "areNotificationsDisabled" | "mileageEmailSentDate">;
  onClose: (success?: boolean) => void;
};

const SendMileageReminderDialog: FC<SendMileageReminderDialogProps> = ({ carId, driver, onClose }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const mileagesApi = useAdminMileagesApi();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [updateMileageEmailSentDate, setUpdateMileageEmailSentDate] = useState(false);

  // the same rules are checked by the server before sending the email
  let driverError: string | undefined;
  if (driver.areNotificationsDisabled) {
    driverError = i18n.t.mileages.reminderDialog.driverNotificationsDisabled(i18n.t.common.disableNotifications);
  } else if (!driver.email) {
    driverError = i18n.t.mileages.reminderDialog.driverNoEmail;
  }

  const onSend = async () => {
    setIsSubmitting(true);
    try {
      await mileagesApi.sendMileageReminder(carId, { updateMileageEmailSentDate });
      dispatch(showSuccessNotification(i18n.t));
      onClose(true);
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalDialog
      onClose={() => onClose()}
      title={i18n.t.mileages.sendReminder}
      isSubmitting={isSubmitting}
      actions={[
        {
          title: i18n.t.common.cancel,
          type: "secondary",
          onClick: () => onClose(),
        },
        {
          title: i18n.t.common.send,
          disabled: isSubmitting || !!driverError,
          onClick: onSend,
        },
      ]}
    >
      <FormFieldsContainer>
        <div>{i18n.t.mileages.questions.sendReminder}</div>

        <FormField
          label={i18n.t.mileages.reminderDialog.recipient}
          isOptional
          error={driverError ? { type: "validate", message: driverError } : undefined}
        >
          <Input disabled value={driver.email ? `${driver.fullName} (${driver.email})` : driver.fullName} />
        </FormField>

        <FormField label={i18n.t.mileages.reminderDialog.lastReminder} isOptional>
          <Input
            disabled
            value={
              driver.mileageEmailSentDate
                ? formatDateNumeric(driver.mileageEmailSentDate, i18n.language)
                : i18n.t.mileages.reminderDialog.noReminderSent
            }
          />
        </FormField>

        <InfoBox text={i18n.t.mileages.reminderDialog.automaticReminderRule} />

        <FormField label={i18n.t.mileages.reminderDialog.updateLastReminder} isOptional>
          {({ labelId }) => (
            <Switch
              id={labelId}
              checked={updateMileageEmailSentDate}
              onChange={setUpdateMileageEmailSentDate}
              disabled={isSubmitting || !!driverError}
            />
          )}
        </FormField>
      </FormFieldsContainer>
    </ModalDialog>
  );
};

export default SendMileageReminderDialog;
